import React, { useCallback, useEffect, useMemo } from 'react';
import { StepWrapper } from '../../../../../components/styled/StepWrapper';
import { ActionButtons } from '../../../ActionButtons/ActionButtons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../store/rootReducer';
import {
  setSideBarSteps,
  setTime,
  setTiming,
  setInitialTiming,
} from '../../../../../store/reducers/appointmentFrameReducer/actions';
import { EAppointmentTimingType } from '../../../../../store/reducers/appointment/types';
import { clearAppointmentSlots } from '../../../../../store/reducers/appointment/actions';
import ReactGA from 'react-ga4';
import { EServiceType } from '../../../../../store/reducers/appointmentFrameReducer/types';
import AppointmentTimingCard from './AppointmentTimingCard/AppointmentTimingCard';
import { useTranslation } from 'react-i18next';
import { TArgCallback, TCallback, TParsableDate } from '../../../../../types/types';
import { TScreen } from '../../../../../types/screens';
import { TimingWrapper } from './styles';
import dayjs from 'dayjs';
import { cardsConfig, timingTypes } from './constants';
import { ETransportationType } from '../../../../../store/reducers/transportationNeeds/types';

type TProps = {
  handleSetScreen: TArgCallback<TScreen>;
  onBack: TCallback;
};

export const AppointmentTiming: React.FC<TProps> = ({ handleSetScreen, onBack }) => {
  const { appointment, isCloneMode, isDemandSmoothMode } = useSelector(
    (state: RootState) => state.appointment
  );
  const { selectedInitialTiming, selectedTime, serviceTypeOption, sideBarSteps, transportation } =
    useSelector((state: RootState) => state.appointmentFrame);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const serviceType = useMemo(() => {
    if (serviceTypeOption) {
      return serviceTypeOption.type;
    }

    return transportation?.type === ETransportationType.PickUpDelivery
      ? EServiceType.PickUpDropOff
      : EServiceType.VisitCenter;
  }, [serviceTypeOption, transportation]);

  const onNext = () => {
    handleSetScreen('appointmentSelection');
  };

  useEffect(() => {
    if (isDemandSmoothMode) {
      handleSelectTiming(EAppointmentTimingType.FirstAvailable);
      onSubmit();
    }
  }, [isDemandSmoothMode]);

  const handleSelectTiming = useCallback(
    (t: EAppointmentTimingType) => () => {
      dispatch(setTiming(t));
      dispatch(setInitialTiming(t));
      if (t === EAppointmentTimingType.FirstAvailable) dispatch(setTime(null));
    },
    []
  );

  const handleChangeTime = useCallback(
    (t: unknown) => {
      const date = dayjs(t as TParsableDate);
      dispatch(setTiming(EAppointmentTimingType.PreferredDate));
      dispatch(setInitialTiming(EAppointmentTimingType.PreferredDate));
      dispatch(setTime(date));
      if (!dayjs.utc(selectedTime).isSame(t as TParsableDate, 'date')) {
        dispatch(clearAppointmentSlots());
      }
    },
    [selectedTime]
  );

  const isTimingValid = Boolean(
    selectedInitialTiming !== null &&
    (selectedInitialTiming !== EAppointmentTimingType.PreferredDate || selectedTime)
  );

  const handleSideBar = () => {
    const index = sideBarSteps.indexOf('appointmentSelection');
    if (index > -1) {
      const slicedSteps = sideBarSteps.slice(0, index + 1);
      dispatch(setSideBarSteps(slicedSteps));
    }
  };

  const handleGA = () => {
    if (selectedInitialTiming) {
      ReactGA.event('search_request_selected', {
        timing_type: timingTypes[selectedInitialTiming],
      });
    }
  };

  const onSubmit = useCallback((): void => {
    handleGA();
    if (appointment?.timingType !== selectedInitialTiming) {
      dispatch(clearAppointmentSlots());
    }
    handleSideBar();
    onNext();
  }, [appointment, dispatch, onNext, selectedInitialTiming, handleGA]);

  const cards = cardsConfig.map(c => ({
    ...c,
    description: t(c.description),
  }));

  return (
    <StepWrapper>
      <TimingWrapper columns={2}>
        {cards.map((card, idx) => {
          if (!idx) {
            return null;
          }
          if (serviceType === EServiceType.PickUpDropOff && idx === 1) {
            // todo delete this when Preferred Date Search will be implemented
            return null;
          }
          return (
            <AppointmentTimingCard
              onClick={handleSelectTiming(card.name)}
              card={card}
              isLoading={false}
              onChangeTime={handleChangeTime}
              selectedTime={
                selectedInitialTiming === EAppointmentTimingType.PreferredDate ? selectedTime : null
              }
              active={selectedInitialTiming === card.name}
              key={card.name}
            />
          );
        })}
      </TimingWrapper>
      <ActionButtons
        hidePrev={isCloneMode}
        onBack={onBack}
        onNext={onSubmit}
        nextDisabled={!isTimingValid}
        nextLabel={t('Next')}
      />
    </StepWrapper>
  );
};
